import { useEffect, useState } from 'react';

/**
 * Design document C7 — desktop-first, tablet-capable.
 *
 * `wide` shows both panels beside the canvas; each narrower step moves one
 * more panel out of the flow, and `compact` becomes a touch layout with bottom
 * sheets and a bottom tool bar.
 */
export type LayoutMode = 'wide' | 'medium' | 'narrow' | 'compact';

/**
 * Chrome costs 48 px of rail plus 280 px per open panel, and the drawing needs
 * about 560 px to be worth having. That puts both panels inline from 1200 px,
 * which keeps a 1280- or 1366-wide laptop out of the overlay modes entirely —
 * the earlier 1400 threshold floated the inspector over the drawing on the
 * commonest screens there are.
 */
export const RAIL_PX = 48;
export const PANEL_PX = 280;
export const MIN_CANVAS_PX = 560;

export const BOTH_PANELS_PX = RAIL_PX + PANEL_PX * 2 + MIN_CANVAS_PX; // 1168
export const ONE_PANEL_PX = RAIL_PX + PANEL_PX + MIN_CANVAS_PX; // 888
export const COMPACT_PX = 760;

export function layoutForWidth(width: number): LayoutMode {
  if (width >= BOTH_PANELS_PX) return 'wide';
  if (width >= ONE_PANEL_PX) return 'medium';
  if (width >= COMPACT_PX) return 'narrow';
  return 'compact';
}

/** Which panels sit in the layout rather than opening over the canvas. */
export function inlinePanels(mode: LayoutMode): { left: boolean; right: boolean } {
  switch (mode) {
    case 'wide':
      return { left: true, right: true };
    case 'medium':
      return { left: true, right: false };
    default:
      return { left: false, right: false };
  }
}

export function useLayoutMode(): LayoutMode {
  const [mode, setMode] = useState<LayoutMode>(() =>
    layoutForWidth(typeof window === 'undefined' ? 1600 : window.innerWidth),
  );
  useEffect(() => {
    const update = (): void => setMode(layoutForWidth(window.innerWidth));
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return mode;
}

/** True when the primary input has no hover — a touch device (C7). */
export function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(
    () => typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches,
  );
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const query = matchMedia('(pointer: coarse)');
    const onChange = (): void => setCoarse(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return coarse;
}
